import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { UserRight } from '@amzn/coral_com-amazon-asxcustomerprofileservice';
import {
  ProductDetailAtfHeroLink,
  ProductViewBase,
} from '@amzn/coral_com-amazon-sssapinterface-model-structures-credits';
import { useBundle } from '@amzn/react-arb-tools';
import {
  Breadcrumbs,
  MODAL_CLOSE_CTA_COLOR_VARIANT,
  MODAL_COLOR_VARIANT,
  MODAL_OVERLAY_VARIANT,
  MODAL_POSITION,
  OFFER_TIER,
  PageSection,
  ProductDetailBanner,
  Tagline,
  useDeviceSize,
  useLogger,
  useModalContext,
  TEASER_CARD_SIZE,
  useApi,
  Loader,
  LOADER_SIZE,
  PRIMARY_BUTTON_VARIANT,
  TeaserCard,
  TeaserCardImage,
  SECTION_THEME,
} from '@amzn/sss-website-theme';

import { useSssApiCreditsClient } from 'src/clients';
import { isUkLaunchEnabled } from 'src/clients/weblab/weblab-client';
import { ErrorFallback } from 'src/components/common/error/ErrorFallback';
import { COMMON_BUNDLE_NAME, PRODUCT_DETAILS_PAGE_BUNDLE_NAME, SEGMENT_PAGE_BUNDLE_NAME } from 'src/constants';
import { OFFER_TIER_BADGE_KEY } from 'src/enums/OfferTierBadgeCopy';
import { ROUTES } from 'src/enums/Routes';
import { getCurrencySymbol, getImageRoot, t } from 'src/helpers';
import { useGetUserRights, useProductTags } from 'src/hooks';
import fallbackImage1 from 'src/images/fallback-image-1.jpg';
import fallbackImage2 from 'src/images/fallback-image-2.jpg';
import fallbackImage3 from 'src/images/fallback-image-3.png';

import styles from './ProductDetailPage.module.scss';
import { LowerCarbonFuelsBtf, MaritimeFuelsBtf, SingleSegmentCreditBtfTemplate } from './below-the-fold';
import { CoteDivoireCreditBtfTemplate } from './below-the-fold/cote-divoire-credit/CoteDivoireCreditBtfTemplate';

const FALLBACK_IMAGES = [fallbackImage1, fallbackImage2, fallbackImage3];

export const ProductDetailPage = () => {
  const { asin } = useParams<{ asin: string }>();
  const navigate = useNavigate();

  const { isAuthenticated, userRights } = useGetUserRights();

  const [productDetailsBundle] = useBundle(PRODUCT_DETAILS_PAGE_BUNDLE_NAME);
  const [segmentPageBundle] = useBundle(SEGMENT_PAGE_BUNDLE_NAME);
  const [commonBundle] = useBundle(COMMON_BUNDLE_NAME);

  const [hasFetchAttempted, setHasFetchAttempted] = useState(false);

  const { openModal } = useModalContext();
  const asxLogger = useLogger();
  const creditsClient = useSssApiCreditsClient();

  const customErrorHandler = (err: Error) => {
    asxLogger.logError('ProductDetailPage', 'Error fetching product data', err, { asin });
  };

  const { isLoading, error, response: productData, callApi } = useApi<ProductViewBase>(customErrorHandler);
  const tags = useProductTags(productData);

  const { isSmallDesktop } = useDeviceSize();
  const imageRoot = getImageRoot(isSmallDesktop);

  useEffect(() => {
    if (!asin) {
      setHasFetchAttempted(true); // Mark as attempted even if no asin
      return;
    }

    const fetchProductData = async () => {
      const response = await creditsClient.getEacProduct(encodeURIComponent(asin));
      return response.product;
    };

    setHasFetchAttempted(true);
    callApi(fetchProductData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asin, creditsClient]);

  let cta;
  if (isAuthenticated && userRights.includes(UserRight.KATHARINE_APPROVED)) {
    // Show CTA for users who are logged in and approved
    cta = {
      label: t(productDetailsBundle, isUkLaunchEnabled ? 'atfBanner_cta_orderCredits' : 'atfBanner_cta_requestCredits'),
      onClick: () => {
        if (!asin) return;

        // TODO: Replace this with dynamic ids from backend API call.
        navigate(ROUTES.CHECKOUT_PAGE.replace(':asin', encodeURIComponent(asin)).replace(':entitlementId?', ''));
      },
    };
  }

  if (isLoading || !hasFetchAttempted) {
    return (
      <div className={styles.loadingMessage}>
        <Loader label={t(productDetailsBundle, 'loading')} size={LOADER_SIZE.LARGE} />
      </div>
    );
  }

  if (!asin || error || !productData) {
    return <ErrorFallback />;
  }

  const displayData = productData;

  // TODO: Implement longer-term solution to conditionally rendering BTF content
  // https://taskei.amazon.dev/tasks/ASX-1284
  const hideBelowTheFoldAsins = ['BT6V8PKRD1', 'BT6V8PKJJF', 'BT6V8VZVNG'];
  const showBelowTheFoldContent =
    displayData.productDetailBtfTemplate !== 'NO_BTF' && !hideBelowTheFoldAsins.includes(asin);

  const currencySymbol = getCurrencySymbol(productData?.productOfferCurrency);

  let bundleMessage;
  if (productData?.productBundleFactor) {
    const bundlePrice = productData.productMatchedOfferBundlePrice ?? productData.productBundleOfferPrice;
    bundleMessage = t(
      productDetailsBundle,
      isUkLaunchEnabled ? 'atfBanner_bundleMessagingLong' : 'atfBanner_bundleMessaging',
      {
        bundleSize: productData.productBundleFactor,
        currencySymbol,
        price: `${bundlePrice}`,
      }
    );
  }

  // Strike-through pricing for non-BASE tier
  const matchedTier = productData.productMatchedOfferTier;
  const basePrice = productData.productBaseOfferPrice;
  const matchedPrice = productData.productMatchedOfferPrice ?? productData.productOfferPrice;
  let originalPrice: number | undefined;
  let badgeCopy: string | undefined;

  if (
    matchedTier &&
    matchedTier !== OFFER_TIER.BASE &&
    basePrice != null &&
    matchedPrice != null &&
    basePrice > matchedPrice
  ) {
    const badgeKey = OFFER_TIER_BADGE_KEY[matchedTier] ?? 'product_signatory_price_tag';
    originalPrice = basePrice;
    badgeCopy = t(productDetailsBundle, badgeKey);
  }

  const openReadMoreModal = () => {
    openModal({
      content: (
        <div>
          <img
            src={fallbackImage3}
            alt={t(productDetailsBundle, 'atfBanner_readMoreImage')}
            className={styles.readMoreImage}
          />
          <div className={styles.readMoreContent}>
            <Tagline>{t(productDetailsBundle, 'atfBanner_readMoreTagline')}</Tagline>
            <h4 className={styles.readMoreHeading}>{t(productDetailsBundle, 'atfBanner_title')}</h4>
            <p>{t(productDetailsBundle, 'atfBanner_readMoreContent')}</p>
          </div>
        </div>
      ),
      position: MODAL_POSITION.LEFT,
      colorVariant: MODAL_COLOR_VARIANT.SUSTAINABILITY_SPRUCE,
      overlayVariant: MODAL_OVERLAY_VARIANT.BLUR,
      closeCtaColorVariant: MODAL_CLOSE_CTA_COLOR_VARIANT.WHITE,
    });
  };

  const resolveCopy = (link: ProductDetailAtfHeroLink) => {
    const fromBundle = link.heroLinkStringId ? t(productDetailsBundle, link.heroLinkStringId) : '';

    // bundle returns "" when missing so we fall back to heroLinkStringCopy
    return fromBundle || link.heroLinkStringCopy || '';
  };

  const scrollToLinks = (productData.productDetailAtfHeroLinks ?? []).map((heroLink) => {
    const base = {
      copy: resolveCopy(heroLink),
      image: {
        src: `${imageRoot}/${heroLink.heroLinkImageUrl}`,
        alt: heroLink.heroLinkStringCopy,
      },
    };

    return heroLink.heroLinkBehavior === 'SCROLL'
      ? { ...base, scrollToSection: heroLink.heroLinkTarget }
      : { ...base, link: heroLink.heroLinkTarget };
  });

  let btfElement;
  if (displayData.productDetailBtfTemplate === 'MARITIME_FUELS_BTF') {
    btfElement = <MaritimeFuelsBtf productDetailsBundle={productDetailsBundle} />;
  } else if (displayData.productDetailBtfTemplate === 'LOWER_CARBON_FUELS_BTF') {
    btfElement = <LowerCarbonFuelsBtf productDetailsBundle={productDetailsBundle} asin={asin} />;
  } else if (displayData.productDetailBtfTemplate === 'REDUCE_DEFORESTATION_BTF') {
    btfElement = (
      <CoteDivoireCreditBtfTemplate
        productDetailsBundle={productDetailsBundle}
        commonBundle={commonBundle}
        segmentPageBundle={segmentPageBundle}
      />
    );
  } else if (displayData.productDetailBtfTemplate === 'SINGLE_SEGMENT_CREDIT_BTF' || showBelowTheFoldContent) {
    // If productDetailBtfTemplate not found but showBelowTheFoldContent is set (legacy fallback behavior), display the
    // SingleSegmentCreditBtfTemplate
    btfElement = <SingleSegmentCreditBtfTemplate productDetailsBundle={productDetailsBundle} />;
  } else if (displayData.productDetailBtfTemplate === 'NO_BTF') {
    // If explicitly set to NO_BTF or if no showBelowTheFoldContent is set to false, display no BTF
    btfElement = null;
  } else {
    btfElement = null;
  }

  return (
    <div>
      <Breadcrumbs
        homepageLink={{ label: t(commonBundle, 'homepage_label'), url: '/' }}
        parentLink={{ label: t(commonBundle, 'carbon_credit_service_label'), url: ROUTES.CREDITS_STORE }}
        currentPage={displayData.productTitle}
      />
      <PageSection className={styles.bannerSection}>
        <ProductDetailBanner
          tags={tags}
          title={displayData.productTitle}
          description={displayData.productDescription ?? t(productDetailsBundle, 'atfBanner_description')}
          price={displayData.productMatchedOfferPrice ?? displayData.productOfferPrice ?? 0}
          originalPrice={originalPrice}
          badgeCopy={badgeCopy}
          currencySymbol={currencySymbol}
          isUkLaunchEnabled={isUkLaunchEnabled}
          image={
            displayData.productDetailAtfHeroImage
              ? `${imageRoot}/${displayData.productDetailAtfHeroImage}`
              : FALLBACK_IMAGES[Math.floor(Math.random() * FALLBACK_IMAGES.length)]
          }
          links={showBelowTheFoldContent ? scrollToLinks : []}
          cta={cta}
          perUnitLabel={t(productDetailsBundle, isUkLaunchEnabled ? 'atfBanner_perCredit' : 'atfBanner_perUnitLabel')}
          perUnitTooltip={t(productDetailsBundle, 'atfBanner_perUnitTooltip')}
          readMoreOnClick={openReadMoreModal}
          customMessage={bundleMessage}
        />
      </PageSection>

      {btfElement}

      {/* Using window.location.origin for now to trigger correct routing since Credits Frontend is a separate app from
      the Sustainability Solutions Hub Website and doesn't support relative paths between the two
        TODO: Replace window.location.origin once apps are combined */}
      {displayData.productDetailBtfTemplate !== 'REDUCE_DEFORESTATION_BTF' && (
        <PageSection theme="squid-ink">
          <TeaserCard
            className={styles.teaserCard}
            size={TEASER_CARD_SIZE.LARGE}
            title={t(productDetailsBundle, 'teaser_title')}
            description={t(productDetailsBundle, 'teaser_description')}
            cta={{
              label: t(productDetailsBundle, 'teaser_ctaLabel'),
              url: ROUTES.CREDITS_LANDING,
              forceServerSideNav: true,
            }}
            theme={SECTION_THEME.Squid_Ink}
          >
            <>
              <TeaserCardImage
                image={`${imageRoot}/credits-segments-restoration-hero-1.jpg`}
                cardTitle={t(productDetailsBundle, 'teaser_restoringForests')}
                cta={t(productDetailsBundle, 'teaser_learnMoreCta')}
                linkUrl={ROUTES.CREDITS_SEGMENT_RESTORING_FORESTS}
                alt={t(productDetailsBundle, 'teaser_manInForest_alt')}
                theme={SECTION_THEME.Pistachio}
                buttonVariant={PRIMARY_BUTTON_VARIANT.Inverted}
                isSquare
                forceServerSideNav
              />
              <TeaserCardImage
                image={`${imageRoot}/credits-segments-deforestation-hero-1.jpg`}
                cardTitle={t(productDetailsBundle, 'teaser_reducingDeforestation')}
                cta={t(productDetailsBundle, 'teaser_learnMoreCta')}
                linkUrl={ROUTES.CREDITS_SEGMENT_REDUCE_DEFORESTATION}
                alt={t(productDetailsBundle, 'teaser_reducingDeforestation_alt')}
                theme={SECTION_THEME.Pistachio}
                buttonVariant={PRIMARY_BUTTON_VARIANT.Inverted}
                isSquare
                forceServerSideNav
              />
              <TeaserCardImage
                image={`${imageRoot}/credits-segments-tcr-hero-1.jpg`}
                cardTitle={t(productDetailsBundle, 'teaser_technologicalCarbonRemoval')}
                cta={t(productDetailsBundle, 'teaser_learnMoreCta')}
                linkUrl={ROUTES.CREDITS_SEGMENT_TECH_CARBON_REMOVAL}
                alt={t(productDetailsBundle, 'teaser_technologicalCarbonRemoval_alt')}
                theme={SECTION_THEME.Pistachio}
                buttonVariant={PRIMARY_BUTTON_VARIANT.Inverted}
                isSquare
                forceServerSideNav
              />
            </>
          </TeaserCard>
        </PageSection>
      )}
    </div>
  );
};
